import { useEffect, useState } from 'react'
import {
    ArrowLeftIcon,
    CheckCircleIcon,
    ClockIcon,
    PencilSquareIcon,
    PlusIcon,
    SparklesIcon,
    TrashIcon,
} from '@heroicons/react/24/outline'
import { Link, useParams } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { api, api_patch } from '@/services/api.service'

import Loading from '@/components/Loading'

import { useProviderSession } from './ProviderSession'

interface RoadmapTask {
    task: string
    isChecked: boolean
}

interface RoadmapStep {
    _id: string
    title: string
    description: string | null
    timeframe: string | null
    order: number | null
    isComplete: boolean
    tasks: RoadmapTask[]
}

// A step in the pending-review batch. Fuller shape than the family-facing step:
// task descriptions are preserved (the family serializer drops them) and there is
// no completion state yet.
interface ReviewTask {
    task: string
    description: string | null
}

interface ReviewStep {
    _id: string
    title: string
    description: string | null
    timeframe: string | null
    order: number | null
    tasks: ReviewTask[]
}

interface RegenerationGuidance {
    guidance: string
    at: string | null
}

interface UnderReview {
    reviewBatchId: string | null
    providerEdited: boolean
    regenerationGuidance: RegenerationGuidance[]
    steps: ReviewStep[]
}

interface ReviewHistoryEntry {
    at: string | null
    action: 'provider.roadmap.edit' | 'provider.roadmap.regenerate' | 'provider.roadmap.approve' | string
    actorEmail: string | null
    actorRole: string | null
}

interface RoadmapResponse {
    consented: boolean
    childName: string | null
    steps?: RoadmapStep[]
    underReview?: UnderReview | null
    reviewHistory?: ReviewHistoryEntry[]
}

type StepStatus = 'completed' | 'in-progress' | 'upcoming'

function stepStatus(step: RoadmapStep): StepStatus {
    if (step.isComplete) return 'completed'
    if (step.tasks.some((t) => t.isChecked)) return 'in-progress'
    return 'upcoming'
}

function formatDate(value?: string | null) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.valueOf())) return null
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Provider view of a family's roadmap. When the org requires review, a newly
// generated roadmap arrives as a pending batch the provider edits / AI-revises /
// approves before the family sees it. Once approved (or when review is off), the
// read-only current roadmap is shown.
export default function ProviderFamilyRoadmap() {
    const session = useProviderSession()
    // Viewer is read-only: it can see a pending roadmap but not edit, revise, or approve.
    const canManage = session?.role === 'admin' || session?.role === 'coordinator'
    const { id } = useParams()
    const [data, setData] = useState<RoadmapResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    async function loadRoadmap() {
        if (!id) return
        const res = await api<RoadmapResponse>(
            `${ROUTES.provider.family}/${id}/roadmap`,
            undefined,
            undefined,
            undefined,
            true
        )
        if (res.error || !res.data) {
            setError(res.error ?? 'Failed to load roadmap')
        } else {
            setData(res.data)
        }
        setLoading(false)
    }

    useEffect(() => {
        loadRoadmap()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    if (loading) {
        return (
            <div className='max-w-3xl mx-auto px-4 py-12'>
                <Loading mode='block' />
            </div>
        )
    }

    if (error || !data) {
        return (
            <div className='max-w-3xl mx-auto px-4 py-12 text-center'>
                <p className='text-neutral-600'>{error ?? 'Client not found.'}</p>
                <Link to='/clients' className='text-brand-600 hover:underline'>
                    Back to clients
                </Link>
            </div>
        )
    }

    const review = data.underReview ?? null
    const steps = data.steps ?? []
    const reviewHistory = data.reviewHistory ?? []

    return (
        <div className='max-w-5xl mx-auto px-4 sm:px-6 py-8'>
            <Link
                to={`/clients/${id}`}
                className='inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900 mb-4'
            >
                <ArrowLeftIcon className='w-4 h-4' />
                Back to {data.childName ?? 'client overview'}
            </Link>

            <div className='mb-6'>
                <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>Roadmap</h1>
                {data.childName && <p className='text-sm text-neutral-600 mt-1'>{data.childName}</p>}
            </div>

            {review && (
                <ReviewPanel
                    licenseId={id!}
                    review={review}
                    canManage={canManage}
                    hasCurrentRoadmap={steps.length > 0}
                    onReviewChange={(next) => setData((prev) => (prev ? { ...prev, underReview: next } : prev))}
                    onApproved={() => {
                        setLoading(true)
                        loadRoadmap()
                    }}
                />
            )}

            <section>
                {review && (
                    <h2 className='text-base font-semibold text-neutral-800 mb-3'>
                        {steps.length > 0 ? 'What the family sees now' : 'Current roadmap'}
                    </h2>
                )}
                {steps.length === 0 ? (
                    <div className='bg-white border border-neutral-200 rounded-2xl p-8 text-center'>
                        <p className='text-sm text-neutral-600'>
                            {review
                                ? 'This family has no roadmap yet. It appears here once you approve the one above.'
                                : 'No roadmap available for this client yet.'}
                        </p>
                    </div>
                ) : (
                    <ul className='space-y-2'>
                        {steps.map((step) => (
                            <StepRow key={step._id} step={step} />
                        ))}
                    </ul>
                )}
            </section>

            {reviewHistory.length > 0 && <ReviewHistoryPanel entries={reviewHistory} />}
        </div>
    )
}

// Audit trail of provider oversight on this family's roadmap: who edited /
// AI-revised / approved it, and when. Visible to every staff role.
const REVIEW_ACTION_LABELS: Record<string, string> = {
    'provider.roadmap.edit': 'Edited a milestone',
    'provider.roadmap.regenerate': 'Revised with AI',
    'provider.roadmap.approve': 'Approved & shared with family',
}

function ReviewHistoryPanel({ entries }: { entries: ReviewHistoryEntry[] }) {
    // Most recent first.
    const ordered = [...entries].reverse()
    return (
        <section className='mt-8'>
            <h2 className='text-base font-semibold text-neutral-800 mb-3'>Review history</h2>
            <ul className='bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100'>
                {ordered.map((e, i) => {
                    const label = REVIEW_ACTION_LABELS[e.action] ?? e.action
                    const Icon =
                        e.action === 'provider.roadmap.approve'
                            ? CheckCircleIcon
                            : e.action === 'provider.roadmap.regenerate'
                              ? SparklesIcon
                              : PencilSquareIcon
                    return (
                        <li key={i} className='flex items-start gap-3 px-4 py-3'>
                            <Icon className='w-5 h-5 text-brand-500 flex-shrink-0 mt-0.5' />
                            <div className='flex-1 min-w-0'>
                                <p className='text-sm text-neutral-800'>{label}</p>
                                <p className='text-xs text-neutral-500'>
                                    {e.actorEmail ?? 'A team member'}
                                    {e.actorRole ? ` · ${e.actorRole}` : ''}
                                    {e.at ? ` · ${formatDateTime(e.at)}` : ''}
                                </p>
                            </div>
                        </li>
                    )
                })}
            </ul>
        </section>
    )
}

function formatDateTime(value: string) {
    const d = new Date(value)
    if (isNaN(d.valueOf())) return ''
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

// The pending-review roadmap: editable steps, an AI-revise box, and approve.
function ReviewPanel({
    licenseId,
    review,
    canManage,
    hasCurrentRoadmap,
    onReviewChange,
    onApproved,
}: {
    licenseId: string
    review: UnderReview
    canManage: boolean
    hasCurrentRoadmap: boolean
    onReviewChange: (next: UnderReview) => void
    onApproved: () => void
}) {
    const [guidance, setGuidance] = useState('')
    const [revising, setRevising] = useState(false)
    const [reviseError, setReviseError] = useState<string | null>(null)
    const [approving, setApproving] = useState(false)
    const [approveError, setApproveError] = useState<string | null>(null)

    async function handleRevise() {
        const text = guidance.trim()
        if (!text) return
        setReviseError(null)
        setRevising(true)
        // suppressForbiddenRedirect: a role-denied 403 should show inline, not
        // bounce the coordinator to /forbidden mid-task.
        const res = await api<{ ok: boolean; underReview: UnderReview }>(
            `${ROUTES.provider.family}/${licenseId}/roadmap/regenerate`,
            { guidance: text },
            undefined,
            120000,
            true
        )
        setRevising(false)
        if (res.status && res.status < 400 && res.data?.underReview) {
            setGuidance('')
            onReviewChange(res.data.underReview)
        } else {
            setReviseError(res.error || 'Failed to revise the roadmap. Please try again.')
        }
    }

    async function handleApprove() {
        setApproveError(null)
        setApproving(true)
        const res = await api(
            `${ROUTES.provider.family}/${licenseId}/roadmap/approve`,
            {},
            undefined,
            undefined,
            true
        )
        if (res.status && res.status < 400) {
            onApproved()
        } else {
            setApproving(false)
            setApproveError(res.error || 'Failed to approve the roadmap. Please try again.')
        }
    }

    function handleStepSaved(updated: ReviewStep) {
        onReviewChange({
            ...review,
            providerEdited: true,
            steps: review.steps.map((s) => (s._id === updated._id ? updated : s)),
        })
    }

    return (
        <div className='bg-amber-50/60 border border-amber-200 rounded-2xl p-5 sm:p-6 mb-8'>
            <div className='flex items-start gap-3 mb-4'>
                <div className='w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0'>
                    <ClockIcon className='w-5 h-5 text-amber-600' />
                </div>
                <div className='flex-1'>
                    <h2 className='text-lg font-semibold text-neutral-900'>Awaiting your review</h2>
                    <p className='text-sm text-neutral-600 mt-0.5'>
                        {canManage
                            ? 'Review this roadmap before the family sees it. Edit any milestone directly, ask AI to revise it, then approve to share it.'
                            : 'This roadmap is awaiting review. Your role has read-only access; an administrator or coordinator can edit and approve it.'}
                        {hasCurrentRoadmap && ' The family keeps their current roadmap until you approve this one.'}
                    </p>
                    {review.providerEdited && (
                        <span className='inline-flex items-center gap-1 mt-2 text-xs font-medium text-amber-700'>
                            <CheckCircleIcon className='w-3.5 h-3.5' />
                            Includes your edits
                        </span>
                    )}
                </div>
            </div>

            <ul className='space-y-2'>
                {review.steps.map((step, index) =>
                    canManage ? (
                        <PendingStepEditor
                            key={step._id}
                            licenseId={licenseId}
                            step={step}
                            index={index}
                            onSaved={handleStepSaved}
                        />
                    ) : (
                        <PendingStepReadOnly key={step._id} step={step} index={index} />
                    )
                )}
            </ul>

            {review.regenerationGuidance.length > 0 && (
                <div className='mt-5 pt-4 border-t border-amber-200'>
                    <h3 className='text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2'>
                        Revision history
                    </h3>
                    <ul className='space-y-1.5'>
                        {review.regenerationGuidance.map((g, i) => (
                            <li key={i} className='text-xs text-neutral-600'>
                                <span className='text-neutral-400'>{formatDate(g.at) ?? '—'}:</span> {g.guidance}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {canManage && (
                <div className='mt-5 pt-5 border-t border-amber-200 space-y-4'>
                    <div>
                        <label className='block text-sm font-medium text-neutral-800 mb-1.5'>
                            Ask AI to revise
                        </label>
                        <textarea
                            rows={3}
                            value={guidance}
                            onChange={(e) => setGuidance(e.target.value)}
                            maxLength={2000}
                            disabled={revising}
                            placeholder='Describe the changes you want (e.g. "Add a step about coordinating with the school" or "Simplify the language for a newly diagnosed family"). Your manual edits are preserved.'
                            className='w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm disabled:opacity-60'
                        />
                        {reviseError && <p className='mt-2 text-sm text-red-600'>{reviseError}</p>}
                        <button
                            onClick={handleRevise}
                            disabled={revising || !guidance.trim()}
                            className='mt-2 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-brand-700 bg-white border border-brand-300 hover:bg-brand-50 rounded-lg disabled:opacity-50'
                        >
                            <SparklesIcon className='w-4 h-4' />
                            {revising ? 'Revising…' : 'Revise with AI'}
                        </button>
                    </div>

                    <div className='flex flex-col sm:flex-row sm:items-center gap-2 pt-1'>
                        <button
                            onClick={handleApprove}
                            disabled={approving || revising}
                            className='inline-flex items-center justify-center gap-1.5 px-5 py-2.5 text-sm font-semibold text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50'
                        >
                            <CheckCircleIcon className='w-4 h-4' />
                            {approving ? 'Approving…' : 'Approve & share with family'}
                        </button>
                        {approveError && <p className='text-sm text-red-600'>{approveError}</p>}
                    </div>
                </div>
            )}
        </div>
    )
}

// Editable card for one pending step. Local draft; saves via PATCH per step.
function PendingStepEditor({
    licenseId,
    step,
    index,
    onSaved,
}: {
    licenseId: string
    step: ReviewStep
    index: number
    onSaved: (updated: ReviewStep) => void
}) {
    const [title, setTitle] = useState(step.title)
    const [description, setDescription] = useState(step.description ?? '')
    const [timeframe, setTimeframe] = useState(step.timeframe ?? '')
    const [tasks, setTasks] = useState<ReviewTask[]>(step.tasks)
    const [saving, setSaving] = useState(false)
    const [saved, setSaved] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Re-seed the draft when the batch is replaced (e.g. after an AI revision
    // swaps in new step documents with new ids).
    useEffect(() => {
        setTitle(step.title)
        setDescription(step.description ?? '')
        setTimeframe(step.timeframe ?? '')
        setTasks(step.tasks)
        setSaved(false)
        setError(null)
    }, [step._id])

    const dirty =
        title !== step.title ||
        description !== (step.description ?? '') ||
        timeframe !== (step.timeframe ?? '') ||
        JSON.stringify(tasks) !== JSON.stringify(step.tasks)

    function updateTask(i: number, value: string) {
        setTasks((prev) => prev.map((t, idx) => (idx === i ? { ...t, task: value } : t)))
        setSaved(false)
    }

    function removeTask(i: number) {
        setTasks((prev) => prev.filter((_, idx) => idx !== i))
        setSaved(false)
    }

    function addTask() {
        setTasks((prev) => [...prev, { task: '', description: null }])
        setSaved(false)
    }

    async function save() {
        if (!title.trim()) {
            setError('A title is required')
            return
        }
        setError(null)
        setSaving(true)
        const body = {
            stepId: step._id,
            title: title.trim(),
            description,
            timeframe,
            tasks: tasks
                .filter((t) => t.task.trim())
                .map((t) => ({ task: t.task.trim(), description: t.description ?? '' })),
        }
        const res = await api_patch<{ ok: boolean; step: ReviewStep }>(
            `${ROUTES.provider.family}/${licenseId}/roadmap`,
            body
        )
        setSaving(false)
        if (res.status && res.status < 400 && res.data?.step) {
            setSaved(true)
            onSaved(res.data.step)
        } else {
            setError(res.error || 'Failed to save changes')
        }
    }

    return (
        <li className='bg-white border border-neutral-200 rounded-xl p-4'>
            <div className='flex items-start gap-3'>
                <span className='w-6 h-6 rounded-full bg-neutral-100 text-neutral-600 text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-1'>
                    {index + 1}
                </span>
                <div className='flex-1 min-w-0 space-y-2'>
                    <input
                        type='text'
                        value={title}
                        onChange={(e) => {
                            setTitle(e.target.value)
                            setSaved(false)
                        }}
                        maxLength={200}
                        placeholder='Milestone title'
                        className='w-full px-3 py-2 border border-neutral-300 rounded text-sm font-medium'
                    />
                    <input
                        type='text'
                        value={timeframe}
                        onChange={(e) => {
                            setTimeframe(e.target.value)
                            setSaved(false)
                        }}
                        maxLength={120}
                        placeholder='Timeframe (e.g. "First 1–2 weeks")'
                        className='w-full px-3 py-1.5 border border-neutral-300 rounded text-xs'
                    />
                    <textarea
                        rows={2}
                        value={description}
                        onChange={(e) => {
                            setDescription(e.target.value)
                            setSaved(false)
                        }}
                        maxLength={2000}
                        placeholder='Description'
                        className='w-full px-3 py-2 border border-neutral-300 rounded text-xs'
                    />

                    <div>
                        <span className='block text-xs font-medium text-neutral-500 mb-1'>Tasks</span>
                        <div className='space-y-1.5'>
                            {tasks.map((t, i) => (
                                <div key={i} className='flex items-center gap-1.5'>
                                    <input
                                        type='text'
                                        value={t.task}
                                        onChange={(e) => updateTask(i, e.target.value)}
                                        maxLength={300}
                                        placeholder='Task'
                                        className='flex-1 px-3 py-1.5 border border-neutral-300 rounded text-xs'
                                    />
                                    <button
                                        type='button'
                                        onClick={() => removeTask(i)}
                                        aria-label='Remove task'
                                        className='p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded'
                                    >
                                        <TrashIcon className='w-4 h-4' />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            type='button'
                            onClick={addTask}
                            className='mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700'
                        >
                            <PlusIcon className='w-3.5 h-3.5' />
                            Add task
                        </button>
                    </div>

                    {error && <p className='text-xs text-red-600'>{error}</p>}

                    <div className='flex items-center gap-2 pt-1'>
                        <button
                            onClick={save}
                            disabled={saving || !dirty}
                            className='px-3 py-1.5 text-xs font-medium text-white bg-neutral-800 hover:bg-neutral-900 rounded-lg disabled:opacity-40'
                        >
                            {saving ? 'Saving…' : 'Save changes'}
                        </button>
                        {saved && !dirty && (
                            <span className='inline-flex items-center gap-1 text-xs text-green-700'>
                                <CheckCircleIcon className='w-3.5 h-3.5' />
                                Saved
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </li>
    )
}

// Read-only rendering of a pending step for the viewer role.
function PendingStepReadOnly({ step, index }: { step: ReviewStep; index: number }) {
    return (
        <li className='bg-white border border-neutral-200 rounded-xl p-4'>
            <div className='flex items-start gap-3'>
                <span className='w-6 h-6 rounded-full bg-neutral-100 text-neutral-600 text-xs font-semibold flex items-center justify-center flex-shrink-0'>
                    {index + 1}
                </span>
                <div className='flex-1 min-w-0'>
                    <div className='flex flex-wrap items-baseline gap-x-2 mb-1'>
                        <span className='text-sm font-medium text-neutral-900'>{step.title}</span>
                        {step.timeframe && <span className='text-xs text-neutral-500'>{step.timeframe}</span>}
                    </div>
                    {step.description && <p className='text-xs text-neutral-600'>{step.description}</p>}
                    {step.tasks.length > 0 && (
                        <ul className='mt-2 space-y-1'>
                            {step.tasks.map((t, i) => (
                                <li key={i} className='text-xs text-neutral-600 flex items-start gap-1.5'>
                                    <span className='text-neutral-300 mt-0.5'>•</span>
                                    {t.task}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </li>
    )
}

function StepRow({ step }: { step: RoadmapStep }) {
    const completedTasks = step.tasks.filter((t) => t.isChecked).length

    return (
        <li className='bg-white border border-neutral-200 rounded-xl p-4'>
            <div className='flex items-start gap-3'>
                <StatusPill status={stepStatus(step)} />
                <div className='flex-1 min-w-0'>
                    <div className='flex flex-wrap items-baseline gap-x-2 mb-1'>
                        <span className='text-sm font-medium text-neutral-900'>{step.title}</span>
                        {step.timeframe && <span className='text-xs text-neutral-500'>{step.timeframe}</span>}
                    </div>
                    {step.description && <p className='text-xs text-neutral-600'>{step.description}</p>}
                    {step.tasks.length > 0 && (
                        <p className='text-xs text-neutral-500 mt-1.5'>
                            {completedTasks} of {step.tasks.length} tasks complete
                        </p>
                    )}
                </div>
            </div>
        </li>
    )
}

function StatusPill({ status }: { status: StepStatus }) {
    if (status === 'completed') {
        return (
            <div className='w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0'>
                <CheckCircleIcon className='w-4 h-4 text-green-600' />
            </div>
        )
    }
    if (status === 'in-progress') {
        return (
            <div className='w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0'>
                <ClockIcon className='w-4 h-4 text-amber-600' />
            </div>
        )
    }
    return <div className='w-6 h-6 rounded-full border-2 border-neutral-300 flex-shrink-0' />
}
